import React, { useCallback, useMemo, useState } from 'react';
import { usePortfolioSelection } from 'portfolio/store/hooks';
import { useActiveBlotterFilteredOrders } from 'widgets/blotter/store/filteredOrders/hooks';
import './TradeOptimizer.scss';
import {
    TextInput,
    Button,
    Modal,
} from '@instinet-analytics/semantic-ui-components';
import { textUtils } from '@instinet-analytics/core-lib';
import { useTradeOptimizerPopup } from 'widgets/tradeOptimizer/tradeOptimizerPopup';
import {
    useDispatchShowInfo,
    useDispatchShowWarning,
} from 'notification/store/dispatcher';
import { useLogger } from 'logging/writers/hooks';
import Order from 'communication/types/order/Order';
import {
    AddBasketPayload,
    AuthenticationStatusChangedMessagePayload,
    ReceiveMessageErrorPayload,
    TradeOptimizerAuthStatus,
} from 'widgets/tradeOptimizer/types';
import classNames from 'classnames';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';

const { pluralise } = textUtils;

export default function TradeOptimizer() {
    const selection = usePortfolioSelection();
    const selectedOrders = selection.selectedOrders;
    const filteredPortfolio = useActiveBlotterFilteredOrders();
    const showWarning = useDispatchShowWarning();
    const showInfo = useDispatchShowInfo();
    const logger = useLogger('TradeOptimizer');
    const [authStatus, setAuthStatus] =
        useState<TradeOptimizerAuthStatus>('idle');

    const [url, setUrl] = useState(
        window.localStorage.getItem('tradeOptUrl') ??
            window.location.host.indexOf('localhost') > -1
            ? 'http://localhost:3010/?showLogonForm=true'
            : deriveTradeOptimizerUrl()
    );

    const [launched, setLaunched] = useState(false);
    const [error, setError] = useState<
        { message: string; payload: ReceiveMessageErrorPayload } | undefined
    >();

    const onSendBasketError = useCallback(
        (payload: ReceiveMessageErrorPayload) => {
            const addBasketPayload = payload.payload as AddBasketPayload;
            const length = addBasketPayload.orders.length;
            const message = length
                ? `Error sending payload of ${length} orders`
                : `Error sending ${payload.topic} message`;
            showWarning(message);
            logger.warn('Error sending message', payload);
            setError({ message, payload });
        },
        [logger, showWarning]
    );

    const onAuthenticationStatusChanged = useCallback(
        (payload: AuthenticationStatusChangedMessagePayload) => {
            setAuthStatus(payload.status);
        },
        [setAuthStatus]
    );

    const popupOptions = useMemo(
        () => ({ onSendBasketError, onAuthenticationStatusChanged }),
        [onSendBasketError, onAuthenticationStatusChanged]
    );

    const popup = useTradeOptimizerPopup(popupOptions);

    const onSetUrl = (t: string) => {
        window.localStorage.setItem('tradeOptUrl', t);
        setUrl(t);
    };

    const onClickLaunch = () => {
        try {
            popup!.openPopup(url);
        } catch (err) {
            logger.critical('Error opening Trade Optimizer', err);
            showWarning('Error opening popup');
        }
        setLaunched(true);
    };

    const sendBasket = (basket: Order[]) => {
        if (!popup!.isOpen) {
            setLaunched(false);
            showInfo('Popup is not open');
            return;
        }
        try {
            popup!.sendBasket(basket);
            logger.debug(
                `Sent basket of ${basket.length} orders to Trade Optimizer`
            );
            showInfo(
                `Sent ${basket.length} ${pluralise(basket.length, 'order')}`
            );
        } catch (err) {
            logger.critical('Error sending basket to Trade Optimizer', err);
            showWarning('Error sending bucket');
        }
    };

    const sendSelectedOrders = () => {
        sendBasket(selectedOrders);
    };
    const sendPortfolio = () => {
        sendBasket(filteredPortfolio);
    };

    const authStatusDesc =
        authStatus === 'idle'
            ? 'Not Logged In'
            : authStatus === 'pending'
            ? 'Logging In'
            : authStatus === 'failed'
            ? 'Login failed'
            : 'Logged in';

    const authStatusClassNames = classNames('auth-status', authStatus);
    return (
        <div className="TradeOptimizer">
            <div className="launcher">
                <div className="url">
                    <div className="label">Url:</div>
                    <TextInput
                        value={url}
                        onChange={onSetUrl}
                        className="url-input"
                    />{' '}
                    <div className={authStatusClassNames}>{authStatusDesc}</div>
                </div>
                <Button primary onClick={onClickLaunch}>
                    Launch App
                </Button>
            </div>
            {launched && (
                <div className="launched">
                    <div>
                        <div className="orders-text">
                            {selectedOrders.length} selected&nbsp;
                            {pluralise(selectedOrders.length, 'order')}
                        </div>
                        <Button
                            primary
                            onClick={sendSelectedOrders}
                            disabled={selectedOrders.length === 0}
                        >
                            Send
                        </Button>
                    </div>
                    <div>
                        <div className="orders-text">
                            {filteredPortfolio.length}&nbsp;
                            {pluralise(filteredPortfolio.length, 'order')} in
                            portfolio
                        </div>
                        <Button
                            primary
                            onClick={sendPortfolio}
                            disabled={filteredPortfolio.length === 0}
                        >
                            Send
                        </Button>
                    </div>
                    {error && (
                        <div>
                            <Modal
                                trigger={<span>{error.message}...</span>}
                                onSave={() => {
                                    return;
                                }}
                                saveText={'OK'}
                                headerText={'Message Error Payload'}
                                className={'tradeopt-message-error'}
                            >
                                <JsonPanel data={error.payload} />{' '}
                            </Modal>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function deriveTradeOptimizerUrl() {
    return `${window.location.protocol}//${window.location.hostname}:28000/TradeOptimizer/?showLogonForm=true`;
}
